import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import {
  adminService,
  AdminApiError,
  type AdminUser,
  type SessionStats,
} from '@/services/adminService';
import { useAdminApi } from '@/hooks/useAdminApi';

type FilterType = 'all' | 'active' | 'pending' | 'admin';
type RoleChange = { user: AdminUser; role: 'admin' | 'member' } | null;

export default function AdminUsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [sessionStats, setSessionStats] = useState<SessionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  const [confirmDelete, setConfirmDelete] = useState<AdminUser | null>(null);
  const [confirmRole, setConfirmRole] = useState<RoleChange>(null);
  const [deleting, setDeleting] = useState(false);
  const [changingRole, setChangingRole] = useState(false);
  const [cleaningSessions, setCleaningSessions] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const { handleError } = useAdminApi();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [userData, sessions] = await Promise.all([
        adminService.getUsers(),
        adminService.getSessionStats(),
      ]);
      setUsers(userData);
      setSessionStats(sessions);
    } catch (err) {
      if (!handleError(err)) {
        setError(err instanceof Error ? err.message : '구성원 정보를 불러오지 못했습니다.');
      }
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredUsers = useMemo(() => {
    let result = users;
    if (filter === 'active') result = result.filter((user) => user.status === 'active');
    if (filter === 'pending') result = result.filter((user) => user.status === 'pending');
    if (filter === 'admin') result = result.filter((user) => user.role === 'admin');

    if (search.trim()) {
      const query = search.trim().toLowerCase();
      result = result.filter(
        (user) => user.name.toLowerCase().includes(query) || user.email.toLowerCase().includes(query),
      );
    }
    return result;
  }, [users, filter, search]);

  const isSelf = (user: AdminUser) => currentUser?.email === user.email;
  const currentIsOwner = Boolean(currentUser?.isOwner);

  const canDelete = (user: AdminUser) => {
    if (isSelf(user) || user.isOwner) return false;
    if (user.role === 'admin' && !currentIsOwner) return false;
    return true;
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    setError('');
    try {
      await adminService.deleteUser(confirmDelete.id);
      setNotice(`${confirmDelete.name}님을 삭제했습니다.`);
      setConfirmDelete(null);
      await fetchData();
    } catch (err) {
      if (!handleError(err)) setError(err instanceof Error ? err.message : '사용자 삭제에 실패했습니다.');
    } finally {
      setDeleting(false);
    }
  };

  const handleRoleChange = async () => {
    if (!confirmRole) return;
    setChangingRole(true);
    setError('');
    try {
      await adminService.updateUserRole(confirmRole.user.id, confirmRole.role);
      setNotice(
        confirmRole.role === 'admin'
          ? `${confirmRole.user.name}님에게 관리자 권한을 부여했습니다. 다시 로그인하면 적용됩니다.`
          : `${confirmRole.user.name}님을 일반 구성원으로 변경했습니다. 다시 로그인하면 적용됩니다.`,
      );
      setConfirmRole(null);
      await fetchData();
    } catch (err) {
      if (!handleError(err)) {
        setError(err instanceof AdminApiError ? err.message : '권한 변경에 실패했습니다.');
      }
    } finally {
      setChangingRole(false);
    }
  };

  const handleSessionCleanup = async () => {
    setCleaningSessions(true);
    setError('');
    try {
      const result = await adminService.cleanupSessions();
      setNotice(
        `세션 정리 완료: 만료 ${result.expiredDeleted}개, 초과 ${result.excessDeleted}개 삭제 · 현재 ${result.activeSessions}개`,
      );
      setSessionStats({
        activeSessions: result.activeSessions,
        usersWithSessions: result.usersWithSessions,
        maxSessionsPerUser: result.maxSessionsPerUser,
      });
    } catch (err) {
      if (!handleError(err)) setError(err instanceof Error ? err.message : '세션 정리에 실패했습니다.');
    } finally {
      setCleaningSessions(false);
    }
  };

  const filters: { key: FilterType; label: string }[] = [
    { key: 'all', label: '전체' },
    { key: 'active', label: '활성' },
    { key: 'pending', label: '대기' },
    { key: 'admin', label: '관리자' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-accent-300 border-t-accent-500 rounded-full animate-spin" />
          <p className="text-sm text-foreground-500">불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-background-50">
      <header className="px-4 pt-12 pb-4">
        <Link to="/admin" className="inline-flex items-center gap-1 mb-4 text-sm text-foreground-500 hover:text-foreground-700">
          <i className="ri-arrow-left-s-line" />
          대시보드
        </Link>
        <h1 className="text-2xl font-heading font-bold text-foreground-950">구성원·권한 관리</h1>
        <p className="text-sm text-foreground-500 mt-2">전체 {users.length}명의 구성원</p>
      </header>

      {(notice || error) && (
        <section className="px-4 pb-4">
          <div className={`rounded-xl px-4 py-3 text-sm ${error ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-700'}`}>
            {error || notice}
          </div>
        </section>
      )}

      <section className="px-4 pb-4">
        <div className="rounded-2xl bg-white p-4 border border-background-100">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-foreground-900">로그인 세션</p>
              <p className="mt-1 text-xs text-foreground-500">
                현재 {sessionStats?.activeSessions ?? 0}개 · 사용자 {sessionStats?.usersWithSessions ?? 0}명 · 1인 최대 {sessionStats?.maxSessionsPerUser ?? 5}개
              </p>
            </div>
            <button
              type="button"
              onClick={handleSessionCleanup}
              disabled={cleaningSessions}
              className="flex-shrink-0 rounded-xl bg-secondary-100 px-3 py-2 text-xs font-semibold text-foreground-700 disabled:opacity-50"
            >
              {cleaningSessions ? '정리 중…' : '세션 정리'}
            </button>
          </div>
        </div>
      </section>

      {!currentIsOwner && (
        <section className="px-4 pb-4">
          <div className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-xs leading-5 text-amber-800">
            관리자 추가·해제는 최고 관리자만 할 수 있습니다. 일반 관리자는 가입 승인과 콘텐츠 관리를 계속 사용할 수 있습니다.
          </div>
        </section>
      )}

      <section className="px-4 pb-3">
        <div className="relative">
          <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
          <input
            type="text"
            placeholder="이름 또는 이메일로 검색"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-300"
          />
        </div>
      </section>

      <section className="px-4 pb-4">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          {filters.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setFilter(item.key)}
              className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-medium ${
                filter === item.key ? 'bg-accent-500 text-white' : 'bg-white text-foreground-600'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </section>

      <section className="px-4 pb-8">
        {filteredUsers.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center text-sm text-foreground-500">검색 결과가 없습니다.</div>
        ) : (
          <div className="space-y-3">
            {filteredUsers.map((user) => {
              const self = isSelf(user);
              const deleteAllowed = canDelete(user);
              return (
                <div key={user.id} className="bg-white rounded-2xl p-4 border border-background-100">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-foreground-600">{user.name.charAt(0)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="text-sm font-semibold text-foreground-900 truncate">
                          {user.name}{self && <span className="ml-1 text-[10px] text-foreground-400 font-normal">(나)</span>}
                        </p>
                        {user.isOwner ? (
                          <span className="px-1.5 py-0.5 rounded-full bg-violet-100 text-[10px] font-semibold text-violet-700">최고 관리자</span>
                        ) : user.role === 'admin' ? (
                          <span className="px-1.5 py-0.5 rounded-full bg-accent-100 text-[10px] font-semibold text-accent-600">관리자</span>
                        ) : null}
                      </div>
                      <p className="text-xs text-foreground-400 truncate">{user.email}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(user)}
                      disabled={!deleteAllowed}
                      title={deleteAllowed ? '삭제' : '이 계정은 삭제할 수 없습니다'}
                      className={`w-8 h-8 flex items-center justify-center rounded-lg ${
                        deleteAllowed ? 'bg-rose-50 text-rose-500' : 'bg-secondary-50 text-foreground-300 cursor-not-allowed'
                      }`}
                    >
                      <i className="ri-delete-bin-line text-sm" />
                    </button>
                  </div>

                  <div className="flex items-center gap-3 mt-3 pt-3 border-t border-background-100 flex-wrap">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                      user.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {user.status === 'active' ? '활성' : '대기 중'}
                    </span>
                    <span className="text-[10px] text-foreground-400">가입: {user.joinedAt}</span>
                    {currentIsOwner && user.status === 'active' && !self && !user.isOwner && (
                      <button
                        type="button"
                        onClick={() => setConfirmRole({ user, role: user.role === 'admin' ? 'member' : 'admin' })}
                        className="ml-auto rounded-lg bg-accent-50 px-2.5 py-1.5 text-[11px] font-semibold text-accent-700"
                      >
                        {user.role === 'admin' ? '관리자 해제' : '관리자 지정'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {confirmRole && (
        <ConfirmModal
          title={confirmRole.role === 'admin' ? '관리자 권한 부여' : '관리자 권한 해제'}
          description={
            confirmRole.role === 'admin'
              ? `${confirmRole.user.name}님에게 관리자 권한을 부여할까요? 변경 후 다시 로그인해야 합니다.`
              : `${confirmRole.user.name}님의 관리자 권한을 해제할까요? 변경 후 다시 로그인해야 합니다.`
          }
          confirmText={changingRole ? '변경 중…' : '권한 변경'}
          danger={confirmRole.role === 'member'}
          disabled={changingRole}
          onCancel={() => setConfirmRole(null)}
          onConfirm={handleRoleChange}
        />
      )}

      {confirmDelete && (
        <ConfirmModal
          title="구성원 삭제 확인"
          description={`${confirmDelete.name}님을 구성원 목록에서 삭제할까요? 이 작업은 되돌릴 수 없습니다.`}
          confirmText={deleting ? '삭제 중…' : '삭제하기'}
          danger
          disabled={deleting}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
}

function ConfirmModal({
  title,
  description,
  confirmText,
  danger = false,
  disabled,
  onCancel,
  onConfirm,
}: {
  title: string;
  description: string;
  confirmText: string;
  danger?: boolean;
  disabled: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/40">
      <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-lg">
        <h3 className="text-base font-bold text-foreground-950 mb-2">{title}</h3>
        <p className="text-sm text-foreground-500 mb-6">{description}</p>
        <div className="flex gap-2">
          <button type="button" onClick={onCancel} disabled={disabled} className="flex-1 px-4 py-2.5 rounded-xl bg-secondary-100 text-sm font-medium text-foreground-700 disabled:opacity-50">
            취소
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={disabled}
            className={`flex-1 px-4 py-2.5 rounded-xl text-white text-sm font-medium disabled:opacity-50 ${danger ? 'bg-rose-500' : 'bg-accent-500'}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
